import React, { useMemo, useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import type { Data, Layout, Config } from 'plotly.js';

// Try to import useTheme hook, fallback to local implementation if not available
let useTheme: () => { resolvedTheme: string };
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const themeModule = require('@/hooks/useTheme');
  useTheme = themeModule.useTheme;
} catch {
  // Fallback implementation when useTheme hook is not available
  useTheme = () => {
    const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    return { resolvedTheme: isDark ? 'dark' : 'light' };
  };
}

export interface ChartDataPoint {
  ticker: string;
  dates: string[];
  values: number[];
  valuesWithoutDrip?: number[] | null;  // Optional non-DRIP values for comparison
}

export interface ReturnsChartProps {
  data: ChartDataPoint[];
  initialInvestment?: number;
  height?: number;
  title?: string;
  showDripToggle?: boolean;  // Show toggle for DRIP comparison
}

// Color palette for multiple tickers
const CHART_COLORS = [
  '#2563eb', // blue-600
  '#dc2626', // red-600
  '#16a34a', // green-600
  '#9333ea', // purple-600
  '#ea580c', // orange-600
  '#0891b2', // cyan-600
  '#c026d3', // fuchsia-600
  '#65a30d', // lime-600
  '#0284c7', // sky-600
  '#e11d48', // rose-600
];

export const ReturnsChart: React.FC<ReturnsChartProps> = ({
  data,
  initialInvestment = 100,
  height = 500,
  title = 'Portfolio Value Over Time',
  showDripToggle = true,
}) => {
  // Get theme from hook or fallback
  const { resolvedTheme } = useTheme();
  const [currentTheme, setCurrentTheme] = useState(resolvedTheme);

  // Update theme when it changes (including for fallback implementation)
  useEffect(() => {
    setCurrentTheme(resolvedTheme);

    // Set up MutationObserver to watch for class changes on document.documentElement
    // This handles the case where the fallback is used and theme changes dynamically
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains('dark');
      setCurrentTheme(isDark ? 'dark' : 'light');
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, [resolvedTheme]);

  const isDark = currentTheme === 'dark';

  // Check if any data has non-DRIP values available
  const hasNonDripData = data.some(
    (series) => series.valuesWithoutDrip && series.valuesWithoutDrip.length > 0
  );

  const [showWithoutDrip, setShowWithoutDrip] = useState(false);

  const plotData: Data[] = useMemo(() => {
    const traces: Data[] = [];

    data.forEach((series, index) => {
      const color = CHART_COLORS[index % CHART_COLORS.length];

      // Main line (with DRIP)
      const firstValue = series.values[0] || 1;
      const normalizedValues = series.values.map(
        (value) => (value / firstValue) * initialInvestment
      );

      traces.push({
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: data.length > 1 ? `${series.ticker} (with DRIP)` : 'With DRIP',
        x: series.dates,
        y: normalizedValues,
        line: {
          color: color,
          width: 2,
        },
        hovertemplate:
          `<b>${series.ticker}${hasNonDripData ? ' (with DRIP)' : ''}</b><br>` +
          'Date: %{x}<br>' +
          'Value: $%{y:,.2f}<br>' +
          '<extra></extra>',
      });

      // Non-DRIP line (if toggle is on and data available)
      if (
        showWithoutDrip &&
        series.valuesWithoutDrip &&
        series.valuesWithoutDrip.length > 0
      ) {
        const firstNoDripValue = series.valuesWithoutDrip[0] || 1;
        const normalizedNoDripValues = series.valuesWithoutDrip.map(
          (value) => (value / firstNoDripValue) * initialInvestment
        );

        traces.push({
          type: 'scatter' as const,
          mode: 'lines' as const,
          name: data.length > 1 ? `${series.ticker} (no DRIP)` : 'Without DRIP',
          x: series.dates,
          y: normalizedNoDripValues,
          line: {
            color: color,
            width: 2,
            dash: 'dash',  // Dashed line for non-DRIP
          },
          hovertemplate:
            `<b>${series.ticker} (without DRIP)</b><br>` +
            'Date: %{x}<br>' +
            'Value: $%{y:,.2f}<br>' +
            '<extra></extra>',
        });
      }
    });

    return traces;
  }, [data, initialInvestment, showWithoutDrip, hasNonDripData]);

  // Theme-aware colors
  const themeColors = useMemo(() => ({
    plot_bgcolor: isDark ? '#1f2937' : '#ffffff',
    paper_bgcolor: isDark ? '#1f2937' : '#ffffff',
    fontColor: isDark ? '#f9fafb' : '#1f2937',
    gridColor: isDark ? '#374151' : '#e5e7eb',
    lineColor: isDark ? '#374151' : '#d1d5db',
    tickFontColor: isDark ? '#9ca3af' : '#6b7280',
    axisTitleColor: isDark ? '#d1d5db' : '#4b5563',
    legendFontColor: isDark ? '#e5e7eb' : '#374151',
  }), [isDark]);

  const layout: Partial<Layout> = useMemo(
    () => ({
      title: {
        text: title,
        font: {
          size: 18,
          color: themeColors.fontColor,
        },
      },
      xaxis: {
        title: {
          text: 'Date',
          font: {
            size: 14,
            color: themeColors.axisTitleColor,
          },
        },
        type: 'date',
        tickformat: '%b %Y',
        gridcolor: themeColors.gridColor,
        linecolor: themeColors.lineColor,
        tickfont: {
          color: themeColors.tickFontColor,
        },
        rangeslider: {
          visible: true,
          thickness: 0.05,
        },
      },
      yaxis: {
        title: {
          text: 'Portfolio Value ($)',
          font: {
            size: 14,
            color: themeColors.axisTitleColor,
          },
        },
        tickformat: '$,.0f',
        gridcolor: themeColors.gridColor,
        linecolor: themeColors.lineColor,
        tickfont: {
          color: themeColors.tickFontColor,
        },
        hoverformat: '$.2f',
      },
      legend: {
        orientation: 'h' as const,
        yanchor: 'bottom' as const,
        y: 1.02,
        xanchor: 'right' as const,
        x: 1,
        font: {
          size: 12,
          color: themeColors.legendFontColor,
        },
      },
      hovermode: 'x unified' as const,
      plot_bgcolor: themeColors.plot_bgcolor,
      paper_bgcolor: themeColors.paper_bgcolor,
      margin: {
        l: 70,
        r: 30,
        t: 60,
        b: 80,
      },
      autosize: true,
    }),
    [title, themeColors]
  );

  const config: Partial<Config> = useMemo(
    () => ({
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: [
        'lasso2d',
        'select2d',
        'autoScale2d',
      ] as any[],
      toImageButtonOptions: {
        format: 'png',
        filename: 'returns_chart',
        height: 600,
        width: 1200,
        scale: 2,
      },
    }),
    []
  );

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-muted rounded-lg border border-border"
        style={{ height }}
        role="img"
        aria-label="No chart data available"
      >
        <p className="text-muted-foreground text-lg">No data available to display</p>
      </div>
    );
  }

  return (
    <div
      className="w-full rounded-lg border border-border bg-card shadow-sm overflow-hidden"
      role="img"
      aria-label={`Line chart showing ${data.map((d) => d.ticker).join(', ')} portfolio values over time`}
    >
      {/* Toggle for showing non-DRIP comparison */}
      {showDripToggle && hasNonDripData && (
        <div className="flex items-center justify-end px-4 pt-3 pb-1">
          <label className="flex items-center cursor-pointer">
            <span className="mr-2 text-sm text-muted-foreground">Compare without DRIP</span>
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only"
                checked={showWithoutDrip}
                onChange={(e) => setShowWithoutDrip(e.target.checked)}
              />
              <div
                className={`w-10 h-5 rounded-full shadow-inner transition-colors ${
                  showWithoutDrip ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
              <div
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  showWithoutDrip ? 'translate-x-5' : ''
                }`}
              />
            </div>
          </label>
        </div>
      )}
      <Plot
        data={plotData}
        layout={layout}
        config={config}
        style={{ width: '100%', height }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default ReturnsChart;
